import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';
import { PublicApi, ChamaConfig } from '../services';

interface PublicConfigContextValue {
  config: ChamaConfig | null;
  loading: boolean;
  error: string | null;
  refreshConfig: () => Promise<void>;
}

const PublicConfigContext = createContext<PublicConfigContextValue | undefined>(undefined);

export const PublicConfigProvider = ({ children }: PropsWithChildren) => {
  const [config, setConfig] = useState<ChamaConfig | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const refreshConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await PublicApi.getPublicConfig();
      setConfig(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load public configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshConfig();
  }, [refreshConfig]);

  const value = useMemo<PublicConfigContextValue>(
    () => ({
      config,
      loading,
      error,
      refreshConfig,
    }),
    [config, loading, error, refreshConfig],
  );

  return <PublicConfigContext.Provider value={value}>{children}</PublicConfigContext.Provider>;
};

export const usePublicConfig = () => {
  const context = useContext(PublicConfigContext);

  if (!context) {
    throw new Error('usePublicConfig must be used within a PublicConfigProvider');
  }

  return context;
};

